import { format } from "date-fns";
import { Segmented } from '@/components/ui/segmented';
import React, { useState } from 'react';
import { Search, X, SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface SimpleFilterState {
  searchQuery: string;
  dateFilter: 'all' | 'today' | 'week' | 'month';
  dateRange: { from: Date | null; to: Date | null };
}

export const EMPTY_FILTERS: SimpleFilterState = {
  searchQuery: '',
  dateFilter: 'all',
  dateRange: { from: null, to: null },
};

interface SimpleFiltersProps {
  /** Owned by the page — the search runs on the server */
  filters: SimpleFilterState;
  onFiltersChange: (filters: SimpleFilterState) => void;
  totalResults: number;
  className?: string;
}

export default function SimpleFilters({
  filters,
  onFiltersChange,
  totalResults,
  className
}: SimpleFiltersProps) {
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const updateFilters = (newFilters: Partial<SimpleFilterState>) => {
    onFiltersChange({ ...filters, ...newFilters });
  };

  const handleClearAllFilters = () => {
    onFiltersChange(EMPTY_FILTERS);
    setShowAdvancedFilters(false);
  };

  // Check if any filters are active
  const hasActiveFilters = filters.searchQuery ||
    filters.dateFilter !== 'all' ||
    filters.dateRange.from ||
    filters.dateRange.to;

  const advancedActive = !!(filters.dateRange.from || filters.dateRange.to);
  const dateValue = (d: Date | null) => (d ? d.toISOString().split('T')[0] : '');

  return (
    <div className={cn('space-y-2', className)}>
      {/* One filter row: search, date range, more filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            type="text"
            aria-label="Search prescriptions"
            placeholder="Search patient, phone, medicine, complaint or test…"
            value={filters.searchQuery}
            onChange={(e) => updateFilters({ searchQuery: e.target.value })}
            className="h-8 pl-8 pr-8 text-sm"
          />

          {filters.searchQuery && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => updateFilters({ searchQuery: '' })}
              className="touch-target absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* A second click on the active range clears it back to all dates */}
        <Segmented
          aria-label="Date range"
          options={[
            { value: 'today', label: 'Today' },
            { value: 'week', label: 'This week' },
            { value: 'month', label: 'This month' },
          ]}
          value={filters.dateFilter}
          onChange={v => updateFilters({
            dateFilter: filters.dateFilter === v ? 'all' : v,
            dateRange: { from: null, to: null }
          })}
        />

        <Button
          variant="outline"
          size="sm"
          aria-expanded={showAdvancedFilters}
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          className={cn('gap-1.5', (showAdvancedFilters || advancedActive) && 'border-primary text-primary')}
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filters
          {advancedActive && <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-label="(active)" />}
        </Button>
      </div>

      {/* More filters */}
      {showAdvancedFilters && (
        <div className="rounded-lg border border-border bg-card p-4">
          <fieldset className="min-w-0">
            <legend className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Dates</legend>
            <div className="flex items-center gap-2 max-w-md">
              <Input
                type="date"
                aria-label="From"
                value={dateValue(filters.dateRange.from)}
                onChange={(e) => updateFilters({
                  dateRange: { from: e.target.value ? new Date(e.target.value) : null, to: filters.dateRange.to },
                  dateFilter: 'all',
                })}
                className="h-8 text-sm"
                max={format(new Date(), "yyyy-MM-dd")}
              />
              <span className="text-xs text-muted-foreground">to</span>
              <Input
                type="date"
                aria-label="To"
                value={dateValue(filters.dateRange.to)}
                onChange={(e) => updateFilters({
                  dateRange: { from: filters.dateRange.from, to: e.target.value ? new Date(e.target.value) : null },
                  dateFilter: 'all',
                })}
                className="h-8 text-sm"
                min={dateValue(filters.dateRange.from) || undefined}
                max={format(new Date(), "yyyy-MM-dd")}
              />
            </div>
          </fieldset>
        </div>
      )}

      {/* Results line: count and what is filtering it */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-sm text-muted-foreground">
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 min-w-0">
          <span><span className="font-semibold text-foreground tabular-nums">{totalResults}</span> prescription{totalResults !== 1 ? 's' : ''}</span>
          {hasActiveFilters && (
            <>
              {filters.searchQuery && <><span aria-hidden>·</span><span className="truncate max-w-[14rem]">“{filters.searchQuery}”</span></>}
              {filters.dateFilter !== 'all' && <><span aria-hidden>·</span><span>{filters.dateFilter === 'today' ? 'Today' : filters.dateFilter === 'week' ? 'This week' : 'This month'}</span></>}
              {(filters.dateRange.from || filters.dateRange.to) && <><span aria-hidden>·</span><span>Custom dates</span></>}
              <Button variant="ghost" size="sm" onClick={handleClearAllFilters} className="h-7 px-2 text-primary hover:text-primary">
                Clear all
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
